const EPS = 12; // end-of-scroll tolerance

class CarouselElement extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Get options from attributes
    const productCarousel = this.hasAttribute('product-carousel');
    const cardsPerRow = this.getAttribute('cards-per-row') || 4;
    const cardsPerRowMobile = this.getAttribute('cards-per-row-mobile');
    const mode = this.getAttribute('mode') || 1;
    const peek = (parseInt(this.getAttribute('peek'), 10) || 0) / 100;
    const leftPeek = (parseInt(this.getAttribute('left-peek'), 10) || 0) / 100;

    // Capture children before clearing
    const originalChildren = Array.from(this.children);

    // Clear existing content
    this.innerHTML = '';

    // Build carousel structure: only .carousel__track and nav as direct children
    const track = document.createElement('ul');
    track.className = 'carousel__track hidden-list';
    // Set initial --visible and peek based on window size
    function setVisibleByScreenAndPeek() {
      if (cardsPerRowMobile && document.documentElement.clientWidth < 768) {
        track.style.setProperty('--visible', cardsPerRowMobile);
      } else if (cardsPerRowMobile && document.documentElement.clientWidth <= 1024) {
        track.style.setProperty('--visible', parseInt(cardsPerRowMobile) + 1);
      } else {
        track.style.setProperty('--visible', cardsPerRow);
      }
      // If infinite and peek is set, distribute peek evenly left/right
      if (peek > 0 && (typeof this !== 'undefined' && this.hasAttribute && this.hasAttribute('infinite') && this.getAttribute('nav') === 'arrows')) {
        const halfPeek = peek / 2;
        track.style.setProperty('--peek', halfPeek);
        track.style.setProperty('--left-peek', halfPeek);
      } else {
        track.style.setProperty('--peek', peek > 0 ? peek : 0);
        track.style.setProperty('--left-peek', leftPeek > 0 ? leftPeek : 0);
      }
    }
    setVisibleByScreenAndPeek();

    // Set ARIA attributes
    track.setAttribute('aria-label', 'Carousel Track');
    track.setAttribute('role', 'list');

    // Slides can be passed as child elements or set via JS
    let slides = Array.from(this.querySelectorAll('.carousel__slide'));
    if (slides.length === 0 && this.hasAttribute('slides')) {
      // Optionally, accept a JSON array of HTML strings
      try {
        const slidesAttr = this.getAttribute('slides');
        if (slidesAttr && slidesAttr.trim().startsWith('[')) {
          slides = JSON.parse(slidesAttr).map(html => {
            const div = document.createElement('div');
            div.className = 'carousel__slide';
            div.innerHTML = html;
            return div;
          });
        }
      } catch (e) { 
        console.warn('Invalid slides JSON:', e);
        slides = []; 
      }
    }

    // Add slides
    let slidesToRender = slides.length ? slides.slice() : null;
    if (!slidesToRender) {
      // Use captured children as slides
      slidesToRender = [];
      originalChildren.forEach(child => {
        if (child.tagName && (child.tagName.toLowerCase() === 'script' || child.tagName.toLowerCase() === 'style')) return; // Skip scripts and templates
        const slide = document.createElement('li');
        slide.className = 'carousel__slide';
        if (child.classList.contains('product-card')) {
          child.style.minWidth = '200px';
        }
        if (child.classList.contains('carousel__slide')) {
          slidesToRender.push(child);
        } else {
          slide.appendChild(child);
          slidesToRender.push(slide);
        }
      });
    }

    // For infinite carousel, add fake slides at start and end
    if (this.hasAttribute('infinite') && this.getAttribute('nav') === 'arrows' && slidesToRender.length > 1) {
      // Clone last slide for the start
      const first = slidesToRender[0].cloneNode(true);
      const last = slidesToRender[slidesToRender.length - 1].cloneNode(true);
      // Mark as fake for debugging or styling if needed
      first.classList.add('carousel__slide--fake');
      last.classList.add('carousel__slide--fake');
      track.appendChild(last);
      slidesToRender.forEach(slide => track.appendChild(slide));
      track.appendChild(first);
    } else {
      slidesToRender.forEach(slide => track.appendChild(slide));
    }

    // Navigation type: 'dots' (default) or 'arrows'
    const navType = this.getAttribute('nav') || 'dots';
    let navElem;
    if (navType === 'arrows') {
      navElem = document.createElement('div');
      navElem.className = 'carousel__arrows';

      const left = document.createElement('button');
      left.className = 'carousel__arrow carousel__arrow--left';
      left.setAttribute('aria-label', 'Previous');
      left.innerHTML = '<i class="bi bi-chevron-left" style="color: var(--secondary-text-color);"></i>';

      const right = document.createElement('button');
      right.className = 'carousel__arrow carousel__arrow--right';
      right.setAttribute('aria-label', 'Next');
      right.innerHTML = '<i class="bi bi-chevron-right" style="color: var(--secondary-text-color);"></i>';

      navElem.appendChild(left);
      navElem.appendChild(right);
    } else {
      navElem = document.createElement('ul');
      navElem.className = 'carousel__dots hidden-list';
      navElem.setAttribute('aria-label', 'Carousel Navigation Dots');

    }

    this.appendChild(track);
    this.appendChild(navElem);
    // Optionally, add carousel logic here (navigation, etc.)
    // Ensure DOM is ready before initializing carousel logic
    initCarousel(this, this.hasAttribute('infinite') && navType === 'arrows');

    // Listen for window resize to update --visible and peek
    if (cardsPerRowMobile || peek > 0) {
      this._carouselResizeHandler = () => setVisibleByScreenAndPeek();
      window.addEventListener('resize', this._carouselResizeHandler);
    }
  }

  disconnectedCallback() {
    // Clean up resize event listener if used
    if (this._carouselResizeHandler) {
      window.removeEventListener('resize', this._carouselResizeHandler);
      this._carouselResizeHandler = null;
    }
  }
}

customElements.define('shop-carousel', CarouselElement);

  function initCarousel(carousel, infinite = false) {
    infinite = infinite || (carousel.hasAttribute('infinite') && carousel.getAttribute('nav') === 'arrows');
    const track = carousel.querySelector('.carousel__track');
    const navType = carousel.getAttribute('nav') || 'dots';
    const dotsContainer = carousel.querySelector('.carousel__dots');
    const arrowsContainer = carousel.querySelector('.carousel__arrows');

    // Use a property on the carousel instance to persist idx
    carousel._idx = (typeof carousel._idx === 'number') ? carousel._idx : (infinite ? 1 : 0);
    let idx = carousel._idx;

    function getVisibleCards() {
      // Only include slides that are not hidden (display: none) and have dimensions
      return Array.from(carousel.querySelectorAll('.carousel__track .carousel__slide')).filter(card => {
        return card.offsetWidth > 0 && card.offsetHeight > 0 && getComputedStyle(card).display !== 'none';
      });
    }

    let cards = getVisibleCards();
    if (!track || (!dotsContainer && !arrowsContainer)) return;

    const mode = Number(carousel.getAttribute('mode')) === 1 ? 1 : 2;
    // visible count can be provided via data-visible or CSS variable --visible
    const desktopVisible = parseInt(carousel.getAttribute('cards-per-row'), 10);
    const mobileVisible = parseInt(carousel.getAttribute('cards-per-row-mobile'), 10);

    if (mobileVisible && document.documentElement.clientWidth < 768) {
      track.style.setProperty('--visible', String(mobileVisible));
    } else if (mobileVisible && document.documentElement.clientWidth <= 1024) {
      track.style.setProperty('--visible', parseInt(mobileVisible) + 1);
    } else if (desktopVisible) {
      track.style.setProperty('--visible', String(desktopVisible));
    }

    let layout = measureLayout(track, cards, mode);
    if (navType === 'dots' && dotsContainer) {
      rebuildDots(dotsContainer, layout.pages);
    }

    // Helper to refresh cards/layout/dots before navigation or index calculations
    function refreshLayout() {
      cards = getVisibleCards();
      layout = measureLayout(track, cards, mode);
      if (navType === 'dots' && dotsContainer) {
        rebuildDots(dotsContainer, layout.pages);
      }
    }
    let lockIndex = null;

    if (navType === 'dots' && dotsContainer) {
      dotsContainer.addEventListener('click', (e) => {
        const btn = e.target.closest('.carousel__dot');
        if (!btn) return;
        refreshLayout();
        let targetIdx = parseInt(btn.dataset.index, 10);
        
        // Disable infinite wrapping for dot navigation
        carousel._isWrapping = true;
        
        // For infinite carousels with dots, ensure we stay within real slides
        if (infinite) {
          // Clamp to real slides only (exclude fake slides at indices 0 and length-1)
          targetIdx = Math.max(1, Math.min(targetIdx, layout.offsets.length - 2));
        }
        
        // Update the carousel index and dot immediately for instant visual feedback
        carousel._idx = targetIdx;
        updateActiveDot(targetIdx);
        
        scrollToIndex(targetIdx);
        setTimeout(() => { carousel._isWrapping = false; }, 600);
        
        if (window.repositionAllProductCardDropdowns && carousel.hasAttribute('product-carousel')) {
          window.repositionAllProductCardDropdowns();
        }
      });
    }

    if (navType === 'arrows' && arrowsContainer) {
      const left = arrowsContainer.querySelector('.carousel__arrow--left');
      const right = arrowsContainer.querySelector('.carousel__arrow--right');
      left.addEventListener('click', () => {
        refreshLayout();
        let prevIdx = currentIndex();
        let newIdx;
        
        if (infinite) {
          // If at first real slide, wrap to last real slide
          if (prevIdx <= 1) {
            // Set wrapping flag BEFORE scrolling
            carousel._isWrapping = true;
            carousel._idx = layout.offsets.length - 2;
            scrollToIndex(carousel._idx);
            updateActiveArrow();
            // Clear flag after animation completes
            setTimeout(() => { carousel._isWrapping = false; }, 600);
            if (window.repositionAllProductCardDropdowns && carousel.getAttribute('product-carousel')) {
              window.repositionAllProductCardDropdowns();
            }
            return;
          } else {
            newIdx = prevIdx - 1;
          }
        } else {
          newIdx = Math.max(0, prevIdx - 1);
        }
        
        carousel._idx = newIdx;
        scrollToIndex(carousel._idx);
        updateActiveArrow();
        if (window.repositionAllProductCardDropdowns && carousel.getAttribute('product-carousel')) {
          window.repositionAllProductCardDropdowns();
        }
      });
      right.addEventListener('click', () => {
        refreshLayout();
        let prevIdx = currentIndex();
        let newIdx;
        
        if (infinite) {
          const maxScrollLeft = track.scrollWidth - track.clientWidth;
          
          // If we're at or near the end, wrap around
          if (prevIdx >= layout.offsets.length - 2 || Math.abs(track.scrollLeft - maxScrollLeft) <= EPS) {
            // Set wrapping flag BEFORE scrolling
            carousel._isWrapping = true;
            carousel._idx = 1;
            scrollToIndex(1);
            updateActiveArrow();
            // Clear flag after animation completes
            setTimeout(() => { carousel._isWrapping = false; }, 600);
            if (window.repositionAllProductCardDropdowns && carousel.hasAttribute('product-carousel')) {
              window.repositionAllProductCardDropdowns();
            }
            return;
          } else {
            newIdx = prevIdx + 1;
          }
        } else {
          newIdx = Math.min(layout.pages - 1, prevIdx + 1);
        }
        
        carousel._idx = newIdx;
        scrollToIndex(carousel._idx);
        updateActiveArrow();
        if (window.repositionAllProductCardDropdowns && carousel.hasAttribute('product-carousel')) {
          window.repositionAllProductCardDropdowns();
        }
      });
    }

    // Use instance properties for suppression flags
    carousel._suppressScrollJump = false;
    carousel._justJumped = false;
    track.addEventListener('scroll', function(e) {
      if (carousel._suppressScrollJump) return;
      if (carousel._isWrapping) return; // Skip if currently wrapping

      // If we just jumped, skip this scroll event and clear the flag
      if (carousel._justJumped) {
        carousel._justJumped = false;
        return;
      }

      // Infinite carousel: handle fake-slide jump
      if (infinite) {
        const maxScrollLeft = track.scrollWidth - track.clientWidth;
        
        // If at the first fake slide (scrollLeft == offsets[0]), jump to last real
        if (Math.abs(track.scrollLeft - layout.offsets[0]) <= EPS) {
          carousel._isWrapping = true;
          carousel._suppressScrollJump = true;
          carousel._idx = layout.offsets.length - 2;
          carousel._justJumped = true;
          track.scrollTo({ left: layout.offsets[carousel._idx], behavior: 'smooth' });
          carousel._suppressScrollJump = false;
          setTimeout(() => { carousel._isWrapping = false; }, 500); // Release lock after animation
          return;
        }
        
        // If at max scroll (right edge), wrap to first real slide
        if (Math.abs(track.scrollLeft - maxScrollLeft) <= EPS) {
          carousel._isWrapping = true;
          carousel._suppressScrollJump = true;
          carousel._idx = 1;
          carousel._justJumped = true;
          track.scrollTo({ left: layout.offsets[carousel._idx], behavior: 'smooth' });
          carousel._suppressScrollJump = false;
          setTimeout(() => { carousel._isWrapping = false; }, 500); // Release lock after animation
          return;
        }
      }

      onScroll(e);

      if (window.repositionAllProductCardDropdowns && carousel.hasAttribute('product-carousel')) {
        window.repositionAllProductCardDropdowns();
      }
    }, { passive: true });
    // Only reset lockIndex after scroll animation is done
    track.addEventListener('scrollend', () => {
      refreshLayout();
      lockIndex = null;
      if (navType === 'dots' && dotsContainer) updateActiveDot();
      if (navType === 'arrows' && arrowsContainer) updateActiveArrow();
      if (window.repositionAllProductCardDropdowns && carousel.hasAttribute('product-carousel')) {
        window.repositionAllProductCardDropdowns();
      }
    });

    let scrollEndTimer = null;
    function onScroll() {
      if (scrollEndTimer) clearTimeout(scrollEndTimer);
      if (track._tick) return;
      track._tick = requestAnimationFrame(() => {
        refreshLayout();
        if (navType === 'dots' && dotsContainer) updateActiveDot();
        if (navType === 'arrows' && arrowsContainer) updateActiveArrow();
        if (infinite && idx > layout.pages - 1) {
          idx = 0;
        } else {
          idx = Math.min(layout.pages - 1, idx);
        }
        track._tick = null;
      });
      // Do not reset lockIndex here; only after scrollend
      scrollEndTimer = setTimeout(() => {
        refreshLayout();
        if (navType === 'dots' && dotsContainer) updateActiveDot();
        if (navType === 'arrows' && arrowsContainer) updateActiveArrow();
      }, 120);
    }

    // Observe size changes
    const ro = new ResizeObserver(() => {
      cards = getVisibleCards();
      const prev = currentIndex();
      layout = measureLayout(track, cards, mode);
      if (navType === 'dots' && dotsContainer) {
        rebuildDots(dotsContainer, layout.pages);
      }
      scrollToIndex(Math.min(prev, layout.pages - 1), false);
      if (navType === 'dots' && dotsContainer) updateActiveDot();
      if (navType === 'arrows' && arrowsContainer) updateActiveArrow();
    });
    ro.observe(track);

    // Observe child changes (add/remove slides) and attribute changes (style, class, etc.)
    const mo = new MutationObserver(() => {
      cards = getVisibleCards();
      layout = measureLayout(track, cards, mode);
      if (navType === 'dots' && dotsContainer) {
        rebuildDots(dotsContainer, layout.pages);
      }
      if (navType === 'dots' && dotsContainer) updateActiveDot();
      if (navType === 'arrows' && arrowsContainer) updateActiveArrow();
    });
    mo.observe(track, { 
      childList: true, 
      subtree: true, 
      attributes: true, 
      attributeFilter: ['style', 'class'] 
    });

    // expose an API to change the number of visible cards at runtime
    carousel.setVisible = function (n) {
      const v = Number(n) || 0;
      if (v <= 0) return;
      track.style.setProperty('--visible', String(v));
      // re-measure layout and update UI
      cards = getVisibleCards();
      layout = measureLayout(track, cards, mode);
      if (navType === 'dots' && dotsContainer) rebuildDots(dotsContainer, layout.pages);

      if (navType === 'dots' && dotsContainer) updateActiveDot();
      if (navType === 'arrows' && arrowsContainer) updateActiveArrow();
    };

    if (navType === 'dots' && dotsContainer) updateActiveDot();
    if (navType === 'arrows' && arrowsContainer) updateActiveArrow();

    function measureLayout(trackEl, cardEls, modeVal) {
      // Only use visible cards for layout calculation
      const visibleCards = cardEls.filter(card => card.offsetWidth > 0 && card.offsetHeight > 0 && getComputedStyle(card).display !== 'none');
      if (!visibleCards.length) {
        return { mode: modeVal, perView: 1, pages: 1, offsets: [0] };
      }
      const cs = getComputedStyle(visibleCards[0]);
      const margin = parseFloat(cs.marginLeft) + parseFloat(cs.marginRight);
      const w = visibleCards[0].getBoundingClientRect().width || visibleCards[0].offsetWidth || 1;
      const cw = Math.max(1, w + margin);
      const perView = Math.max(1, Math.floor(trackEl.clientWidth / cw));
      const total = visibleCards.length;
      const baseLeft = visibleCards[0].offsetLeft;
      // Simulate leftside peek by shifting all offsets if leftPeek is set
      let leftPeekPx = 0;
      try {
        // Try to get leftPeek from the carousel attribute (in percent of track width)
        const carousel = trackEl.closest('shop-carousel');
        if (carousel) {
          const leftPeekAttr = (parseFloat(carousel.getAttribute('left-peek')) || 0) / 100;
          if (leftPeekAttr > 0) {
            leftPeekPx = trackEl.clientWidth * leftPeekAttr;
          }
        }
      } catch (e) {}

      if (modeVal === 1) {
        const maxScrollLeft = track.scrollWidth - track.clientWidth;
        const isInfinite = trackEl.closest('shop-carousel')?.hasAttribute('infinite') && trackEl.closest('shop-carousel')?.getAttribute('nav') === 'arrows';
        
        // Build raw offsets
        const rawOffsets = Array.from({ length: visibleCards.length }, (_, i) => {
          let offset = visibleCards[i].offsetLeft - baseLeft;
          
          // For infinite: don't clamp fake slides so they have unique positions
          if (!isInfinite) {
            offset = Math.min(offset, maxScrollLeft);
          }
          
          return offset | 0;
        });
        
        let offsets;
        if (isInfinite) {
          // Keep all offsets including fake slides
          offsets = rawOffsets;
        } else {
          // Deduplicate offsets with a threshold (EPS)
          offsets = [];
          for (let i = 0; i < rawOffsets.length; i++) {
            if (offsets.length === 0 || Math.abs(rawOffsets[i] - offsets[offsets.length - 1]) > EPS) {
              offsets.push(rawOffsets[i]);
            }
          }
        }
        
        // Shift all offsets by leftPeekPx if set
        if (leftPeekPx > 0) {
          offsets = offsets.map(x => Math.max(x - leftPeekPx, 0));
        }
        if (leftPeekPx > 0 && idx === 1 && track.scrollLeft === 0) track.scrollLeft = offsets[1] || 0;
        
        return {
          mode: 1,
          perView,
          pages: offsets.length,
          offsets
        };
      }

      // --- existing mode 2 below ---
      const pages = Math.max(1, Math.ceil(total / perView));
      let offsets = Array.from({ length: pages }, (_, p) => {
        const idx = Math.min(p * perView, total - 1);
        const rawOffset = (visibleCards[idx].offsetLeft - baseLeft) | 0;
        // Clamp each offset to maxScrollLeft
        const maxScrollLeft = track.scrollWidth - track.clientWidth;
        return Math.min(rawOffset, maxScrollLeft);
      });
      // Shift all offsets by leftPeekPx if set
      if (leftPeekPx > 0) {
        offsets = offsets.map(x => x - leftPeekPx);
      }
      return { mode: 2, perView, pages, offsets };
    }

    function rebuildDots(container, count) {
      container.innerHTML = '';


      const trackWidth = track.clientWidth;
      const cardsWidth = cards.reduce((sum, card) => sum + card.offsetWidth, 0);
      const perView = layout.perView;
      const totalVisibleCards = cards.length;
      
      let totalPositions, dotCount;
      if (layout.mode === 1) {
        // Mode 1: single-card scroll
        // If cards fit exactly in track or fewer cards than perView, no dots needed
        if (totalVisibleCards <= perView || cardsWidth <= trackWidth + 1) {
          dotCount = 0;
        } else {
          // For mode 1, dots should equal total scrollable positions
          // Total positions = totalCards - perView + 1
          totalPositions = Math.max(1, totalVisibleCards - perView + 1);
          dotCount = totalPositions;
        }
      } else {
        // Mode 2: page-by-page scroll
        // For mode 2, dots should equal total pages
        totalPositions = Math.ceil(totalVisibleCards / perView);
        dotCount = totalPositions;
      }
  
      if (document.documentElement.clientWidth <= 975) {
        dotCount--;
        totalPositions--;
      }

      // Don't show dots if no scrolling is needed
      if (cardsWidth <= trackWidth + 1 || dotCount <= 1) {
        return;
      }
      
      const frag = document.createDocumentFragment();
      for (let i = 0; i < dotCount; i++) {
        const b = document.createElement('li');
        b.className = 'carousel__dot';
        b.dataset.index = i;
        b.setAttribute('aria-label', `Go to slide ${i + 1}`);
        b.setAttribute('role', 'button');
        frag.appendChild(b);
      }
      container.appendChild(frag);
    }

    function atEnd() {
      const maxScrollLeft = track.scrollWidth - track.clientWidth;

      return Math.abs(track.scrollLeft - maxScrollLeft) <= EPS;
    }
    
    function currentIndex() {
      refreshLayout();
      const x = track.scrollLeft;
      const firstOffset = layout.offsets[0];
      const lastOffset = layout.offsets[layout.offsets.length - 1];

      if (layout.mode === 1) {
        // clamp to the START instead of the END
        if (x <= firstOffset + EPS) return 0

        if (x >= lastOffset - EPS) return layout.offsets.length - 1;
      } else {
        // existing end-clamp logic for mode 2
        if (atEnd())
          return layout.offsets.length - 1;
      }

      // find nearest offset normally
      let nearest = 0;
      let best = Infinity;
      for (let i = 0; i < layout.offsets.length; i++) {
        const d = Math.abs(layout.offsets[i] - x);
        if (d < best) { best = d; nearest = i; }
      }
      return nearest;
    }

    function scrollToIndex(idx, smooth = true) {
      // Normal scroll
      carousel._idx = idx;
      const left = layout.offsets[idx] ?? layout.offsets[1];
      track.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
    }

    function updateActiveDot(targetIdx = null) {
      const idx = targetIdx !== null ? targetIdx : currentIndex();
      dotsContainer.querySelectorAll('.carousel__dot').forEach((d, i) =>
        d.classList.toggle('active', i === idx || (i === layout.offsets.length - 1 && idx === layout.pages - 1))
      );
    }

    function updateActiveArrow() {
      if (!arrowsContainer) return;
      // Hide arrows if all cards fit in the track (no scrolling needed)
      const cardsWidth = cards.reduce((sum, card) => sum + card.offsetWidth, 0);
      if (cardsWidth <= track.clientWidth + 1) {
        arrowsContainer.style.display = 'none';
        return;
      } else {
        arrowsContainer.style.display = '';
      }
      const left = arrowsContainer.querySelector('.carousel__arrow--left');
      const right = arrowsContainer.querySelector('.carousel__arrow--right');
      // Only disable if physically at the ends
      if (infinite) {
        left.disabled = false;
        right.disabled = false;
      } else {
        // At far left
        left.disabled = track.scrollLeft <= EPS;
        // At far right
        right.disabled = idx === layout.pages - 1 || (track.scrollLeft + track.clientWidth >= track.scrollWidth - EPS);
      }
    }
  }

